import type { BehaviorDomain } from '../core/types.js';
import type { RescueKind, RescueModule } from './rescue-types.js';
import { filterModules } from './module-filter.js';
import { RESCUE_MODULES } from './rescue-registry.js';

export interface RecommendInput {
  preferredKinds: string[];
  reducedMotion: boolean;
  durationSec: number;
  triggers?: string[] | undefined;
  recentlyUsedModuleIds?: string[] | undefined;
  /** Module success ratios keyed by module id, 0..1. */
  successByModuleId?: Record<string, number> | undefined;
  /** Active habit domain — used for safety filtering (e.g. gambling). */
  domain?: BehaviorDomain | undefined;
  /** Allow experimental modules (default false). */
  includeExperimental?: boolean | undefined;
  /** Module ids the user starred. Boosted in ranking. */
  favoriteModuleIds?: string[] | undefined;
  /** Cap on returned modules. Omit (or pass 0) to return every safe module. */
  limit?: number | undefined;
}

const TRIGGER_KIND_BIAS: Record<string, RescueKind[]> = {
  stress: ['breathing'],
  anxiety: ['breathing'],
  boredom: ['minigame', 'movement'],
  loneliness: ['reflection'],
  social: ['reflection'],
  after_work: ['movement', 'breathing'],
  before_bed: ['breathing'],
  after_meals: ['movement'],
  celebration: ['reflection'],
  anger: ['breathing', 'movement'],
  sadness: ['reflection'],
  discomfort: ['breathing'],
  habit: ['minigame', 'movement'],
  waking_up: ['breathing'],
};

export function recommend(input: RecommendInput): RescueModule[] {
  const {
    preferredKinds, reducedMotion, durationSec, triggers = [],
    recentlyUsedModuleIds = [], successByModuleId = {}, favoriteModuleIds = [],
  } = input;
  const triggerKinds = new Set<RescueKind>();
  for (const t of triggers) for (const k of (TRIGGER_KIND_BIAS[t] ?? [])) triggerKinds.add(k);
  const favorites = new Set(favoriteModuleIds);

  const candidates = filterModules(RESCUE_MODULES, {
    domain: input.domain,
    reducedMotion,
    includeExperimental: input.includeExperimental,
  });
  const scored = candidates.map((m) => {
    let score = 0;
    if (favorites.has(m.id)) score += 6; // favorites win ties decisively
    if (preferredKinds.includes(m.kind)) score += 5;
    if (triggerKinds.has(m.kind)) score += 3;
    if (reducedMotion && !m.reducedMotion) score -= 3;
    score += 2 - Math.min(2, Math.abs(m.defaultDurationSec - durationSec) / 60);
    const successRate = successByModuleId[m.id];
    if (typeof successRate === 'number') score += successRate * 2;
    if (recentlyUsedModuleIds.includes(m.id)) score -= 1; // novelty bias
    return { m, score };
  });
  scored.sort((a, b) => b.score - a.score);
  const ranked = scored.map((s) => s.m);
  const limit = input.limit ?? 0;
  return limit > 0 ? ranked.slice(0, limit) : ranked;
}
